import { useCallback, useEffect, useRef, useState } from 'react';

export interface PolledData<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean; // true only until the first result (or error) arrives
  refreshing: boolean; // true from a manual refetch() until its result (or error) arrives
  updatedAt: number | null;
  refetch: () => void;
}

interface State<T> {
  data?: T;
  error: string | null;
  loading: boolean;
  refreshing: boolean;
  updatedAt: number | null;
}

/** `restartKey`: when it changes the fetch restarts immediately but the previous data stays visible (stale-while-revalidate). */
export function usePolledData<T>(fetcher: () => Promise<T>, intervalMs?: number, restartKey?: unknown): PolledData<T> {
  const [state, setState] = useState<State<T>>({ error: null, loading: true, refreshing: false, updatedAt: null });
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  const counter = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });
  const refetch = useCallback(() => {
    setState((s) => ({ ...s, refreshing: true }));
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const reqId = ++counter.current;
      inFlight.current = true;
      try {
        const data = await Promise.resolve().then(() => fetcherRef.current());
        if (!cancelled && reqId === counter.current) {
          setState({ data, error: null, loading: false, refreshing: false, updatedAt: Date.now() });
        }
      } catch (e) {
        if (!cancelled && reqId === counter.current) {
          setState((s) => ({
            ...s,
            error: e instanceof Error ? e.message : 'Failed to load',
            loading: false,
            refreshing: false,
          }));
        }
      } finally {
        // Only the latest request owns the flag; an older one finishing must not clear it.
        if (reqId === counter.current) inFlight.current = false;
      }
    };
    void run();
    if (!intervalMs) {
      return () => {
        cancelled = true;
        inFlight.current = false;
      };
    }
    const visible = () => document.visibilityState === 'visible';
    const id = setInterval(() => {
      if (visible() && !inFlight.current) void run();
    }, intervalMs);
    const onVis = () => {
      if (visible()) void run();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      inFlight.current = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs, tick, restartKey]);

  return { data: state.data, error: state.error, loading: state.loading, refreshing: state.refreshing, updatedAt: state.updatedAt, refetch };
}
